import {forward, guard, sample} from 'effector';
import i18next from 'i18next';
import {toast} from 'react-toastify';

import {authBroadcast} from 'core/broadcastAuth';
import {RestError, showRestError} from 'core/errors';
import {TokenStorage} from 'core/tokenStorage';

import {
    authSyncReceived,
    cleanAuthData,
    cleanAuthDataFx,
    createOrganizationFx,
    doLogin,
    doLoginWithCert,
    doLogout,
    loadProfileFx,
    loginError$,
    loginFx,
    loginRedirectFromPrevPath$,
    loginWithCertFx,
    logoutFx,
    otpError$,
    profile$,
    reloadPageFx,
    resetAuthData,
    sendOtpFx,
    tempAuthData$,
    verifyOtpFx,
} from './index';

loginError$.on(loginFx.failData, (_, {message}) => message).reset(loginFx.done);
loginRedirectFromPrevPath$.on(doLogout, (_, payload) => payload);

forward({
    from: doLogin,
    to: loginFx,
});

forward({
    from: doLoginWithCert,
    to: loginWithCertFx,
});

forward({
    from: doLogout.map(() => TokenStorage.refreshToken),
    to: logoutFx,
});

forward({
    from: logoutFx.done,
    to: cleanAuthDataFx,
});

forward({
    from: cleanAuthData,
    to: cleanAuthDataFx,
});

authBroadcast.subscribe((message) => {
    authSyncReceived(message.type);
});

guard({
    clock: authSyncReceived,
    filter: (type) => type === 'LOGIN',
    target: reloadPageFx,
});

loginWithCertFx.doneData.watch(([authData]) => TokenStorage.storeTokens(authData, true));

loginFx.failData.watch((err) => showRestError(err as RestError));
loginWithCertFx.failData.watch((err) => showRestError(err as RestError));
logoutFx.failData.watch((err) => showRestError(err as RestError));
createOrganizationFx.failData.watch((err) => showRestError(err as RestError));

otpError$.reset([verifyOtpFx, sendOtpFx, doLogin]);

profile$
    .on(loadProfileFx.doneData, (_, data) => data)
    .on(loginWithCertFx.doneData, (_, [{profile}]) => profile)
    .reset(cleanAuthDataFx.finally);

sample({
    clock: loginFx.doneData,
    fn: ([authData, saveCredentials]) => ({authData, saveCredentials}),
    target: tempAuthData$,
});

tempAuthData$.reset([resetAuthData, cleanAuthDataFx.finally]);

export const otpSuccessWithData = guard({
    clock: verifyOtpFx.done,
    source: tempAuthData$,
    filter: (tempData) => Boolean(tempData),
});

sample({
    clock: otpSuccessWithData,
    fn: (tempData) => tempData!.authData.profile,
    target: profile$,
});

otpSuccessWithData.watch((tempData) => {
    if (tempData) {
        TokenStorage.storeTokens(tempData.authData, tempData.saveCredentials);
        authBroadcast.notifyLogin();
    }
});

tempAuthData$.on(otpSuccessWithData, () => null);
otpError$.on(verifyOtpFx.failData, () => true);
verifyOtpFx.failData.watch((err) => showRestError(err as RestError));

createOrganizationFx.doneData.watch(() =>
    toast.success(i18next.t('pages.register.message.success')),
);
